import { useState } from "react";

import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";

import Button from "@/components/button";
import Input from "@/components/input";
import OtpInput from "@/components/otp-input";
import { useAuth } from "@/context/auth";
import {
  useConfirmVerificationMutation,
  useStartVerificationMutation,
  useUpdatePhoneMutation,
} from "@/queries/hooks/authHooks";
import { Logger } from "@/utils/logger";

export const Route = createFileRoute("/auth/_layout/verification")({
  beforeLoad: ({ context }) => {
    if (!context.auth?.isAuthenticated) {
      throw redirect({ to: "/auth/login" });
    }
  },
  component: VerificationPage,
});

const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;

function VerificationPage() {
  const { t } = useTranslation("auth");
  const [error, setError] = useState("");
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const auth = useAuth();
  const navigate = useNavigate();

  const userPhone = auth.user?.phone || "";
  const maskedPhone = `****${userPhone.slice(-5)}`;

  const confirmVerificationMutation = useConfirmVerificationMutation("phone");
  const startVerificationMutation = useStartVerificationMutation("phone");
  const updatePhoneMutation = useUpdatePhoneMutation();

  async function confirmVerification(code: string) {
    try {
      await confirmVerificationMutation.mutateAsync({ code });

      // Mark phone as verified and navigate to next step
      auth.markPhoneVerified();
      const userRole = auth.user?.role || "worker";

      // Check if onboarding is completed
      if (auth.user?.onboardingCompleted) {
        await navigate({
          to: "/app/$role/dashboard",
          params: { role: userRole },
        });
      } else {
        await navigate({
          to: "/app/$role/setup",
          params: { role: userRole },
        });
      }
    } catch (err) {
      Logger.error("Confirm verification failed", {
        error: String((err as any)?.message || err),
      });
      setError(t("verify.invalidCode"));
    }
  }

  async function startVerification() {
    try {
      await startVerificationMutation.mutateAsync();
    } catch (err) {
      Logger.error("Start verification failed", {
        error: String((err as any)?.message || err),
      });
      setError(t("verify.failedToSend"));
    }
  }

  function openPhoneEdit() {
    setNewPhone(userPhone);
    setPhoneError("");
    setError("");
    setIsEditingPhone(true);
  }

  async function savePhone() {
    if (!PHONE_REGEX.test(newPhone)) {
      setPhoneError(t("validation.invalidPhone"));
      return;
    }
    try {
      await updatePhoneMutation.mutateAsync({ phone: newPhone });
      auth.updateUser({ phone: newPhone });
      setIsEditingPhone(false);
      setPhoneError("");
      await startVerification();
    } catch (err) {
      Logger.error("Update phone failed", {
        error: String((err as any)?.message || err),
      });
      setPhoneError(t("verify.phoneUpdateFailed"));
    }
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        className="absolute top-0 right-0"
        onClick={auth.logout}
      >
        {t("verify.logout")}
      </Button>
      <h1 className="heading-1 mb-5">{t("verify.phoneTitle")}</h1>
      <img
        src="/email-2fa.svg"
        alt={t("verify.imageAlt")}
        width={60}
        height={60}
        className="mb-6"
      />
      <p className="body mb-1">
        <Trans
          i18nKey="verify.phoneCodeSentTo"
          ns="auth"
          values={{ phone: maskedPhone }}
          components={{ strong: <strong /> }}
        />
      </p>
      <p className="body mb-6">{t("verify.phoneEnterPrompt")}</p>
      <OtpInput
        maxLength={6}
        onComplete={async (code) => {
          await confirmVerification(code);
        }}
      />
      {error && <p className="body-small text-red-500 mt-2">{error}</p>}
      {isEditingPhone ? (
        <div className="mt-4">
          <Input
            label={t("verify.newPhoneLabel")}
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            placeholder="+1234567890"
            hint={phoneError || undefined}
            state={phoneError ? "error" : "default"}
          />
          <div className="flex gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => setIsEditingPhone(false)}
            >
              {t("verify.cancelEdit")}
            </Button>
            <Button
              size="sm"
              type="button"
              onClick={savePhone}
              disabled={updatePhoneMutation.isPending || startVerificationMutation.isPending}
            >
              {t("verify.savePhone")}
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="body-small mt-4 underline cursor-pointer"
          onClick={openPhoneEdit}
        >
          {t("verify.editPhone")} {t("verify.editPhoneLink")}
        </button>
      )}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          await startVerification();
        }}
      >
        <Button variant="link" className="-ml-5 mt-4" type="submit">
          {t("verify.resendConfirmationCode")}
        </Button>
      </form>
    </div>
  );
}
